import { useState, ChangeEvent } from "react";

interface SubmitResponse {
  success: boolean;
  message?: string;
}

interface UseFormSubmitOptions<T> {
  onSubmit: (data: T) => Promise<SubmitResponse>;
  initialData: T;
  validateForm?: (data: T) => boolean;
  successMessage?: string;
  errorMessage?: string;
}

export function useFormSubmit<T>({
  onSubmit,
  initialData,
  validateForm,
  successMessage = "Успешно отправлено",
  errorMessage = "Произошла ошибка при отправке",
}: UseFormSubmitOptions<T>) {
  const [formData, setFormData] = useState<T>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const [submitSuccess, setSubmitSuccess] = useState<string>("");
  const [isAttemptedSubmit, setIsAttemptedSubmit] = useState(false);

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setFormData(initialData);
    setIsAttemptedSubmit(false);
    setSubmitError("");
  };

  const isFormValid = (): boolean => {
    if (validateForm) {
      return validateForm(formData);
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsAttemptedSubmit(true);

    if (!isFormValid()) {
      console.log("Form validation failed");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");
    console.log("Starting submission, form data:", formData);

    try {
      const response = await onSubmit(formData);

      if (response.success) {
        setSubmitSuccess(response.message || successMessage);
        // Reset form data but don't clear success message
        setFormData(initialData);
        setIsAttemptedSubmit(false);
        // Don't call resetForm() as it would reset other states too
      } else {
        console.log("Setting error message:", response.message || errorMessage);
        setSubmitError(response.message || errorMessage);
        resetForm();
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSubmitError(`${errorMessage}. Пожалуйста, попробуйте позже.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    handleInputChange,
    isSubmitting,
    submitError,
    submitSuccess,
    handleSubmit,
    isAttemptedSubmit,
    isFormValid,
    resetForm,
  };
}
